import { Component, Element, Event, EventEmitter, h, Host, Listen, Method, Prop, State } from "@stencil/core";
import { serializeForm, waitForHydrated } from "@trumpf/ux.utils";
import { throttle } from "../../utils/throttle";
import { elementsSelectors } from "./supported-elements-selectors";
import { scrollToTarget } from "../../utils/scrollToTarget";
import api from "../../utils/api";

import {
    getErrorMessageSetter,
    getFormData,
    getInputElements,
    getValidationAttributes,
    handleDisabledElements,
    validateFormGroup
} from "./utils.js";

/**
 * @slot (default) - The slot for the form element including all the input fields
 *                   that need to be validated
 */

@Component({
    tag: "ux-form",
    styleUrl: "form.scss"
})
export class FormValidator {
    private baseClass = "ux-form";
    private formElement: HTMLFormElement;
    /** this is an array of all supported ux form elements:
     * (HTMLUxInputFieldElement | HTMLUxSelectElement | [...])[]
     **/
    /* eslint-disable-next-line @typescript-eslint/no-explicit-any */
    private uxInputElements: any[];
    private isValid = true;
    private skipValidation = false;
    private isValidPassword = false;
    private isConfirmedPassword = false;
    private dataPrefix = "data-validation";
    private dataNovalidation = `${this.dataPrefix}-novalidation`;
    private dataRequired = `${this.dataPrefix}-required`;
    private dataInvalid = `${this.dataPrefix}-invalid`;
    private dataLength = `${this.dataPrefix}-length`;
    private dataValue = `${this.dataPrefix}-value`;
    private dataGroup = `${this.dataPrefix}-group`;
    private dataRegex = `${this.dataPrefix}-regex`;

    /** if set, the validation will occur on element blur */
    @Prop() readonly validationOnBlur: boolean = false;
    /** if set, the validation will not occour */
    @Prop() readonly noValidation: boolean = false;
    /**
     * if set, the form method will be ignored, instead,
     * the formData will be sent asynchronously to the url specified in the
     * form action attribute
     **/
    @Prop() readonly async: boolean = false;
    /** an additional ofset top amout for the automatic form scerollings */
    @Prop() readonly additionalScrollOffset: number = 0;

    @Element() element: HTMLUxFormElement;

    @State() isSubmittingAsync = false;
    @State() asyncError = false;
    @State() customAction: string | null = null;

    /** function that performs the validation of the input elements */
    @Method()
    async validate() {
        this.validateForm();
    }

    /** function that allows to validate just specific elements of the form */
    @Method()
    async validateElements(container: HTMLElement) {
        const elementsToValidate = getInputElements({
            container: container,
            supportedElements: elementsSelectors
        });
        elementsToValidate.forEach((uxInputElement) => {
            this.validateField(uxInputElement);
        });
        this.scrollToFirstError({
            uxElements: elementsToValidate
        });
    }

    /**
     * An event emitted when the form has been validated,
     * if preventDefault is called on the event, the form will not submit,
     * even if there are no validation errors.
     * It contains the following keys: isValid, serialisedFormData, formData.
     */
    @Event({
        eventName: "uxFormValidated",
        composed: true,
        cancelable: true,
        bubbles: true
    })
    formValidationEvent: EventEmitter<{
        isValid: boolean;
        serializedFormData: string;
        formData: {};
    }>;

    componentDidLoad() {
        this.initForm();
    }

    private initForm = () => {
        this.formElement = this.element.querySelector("form");
        this.skipValidation = this.noValidation || this.formElement?.hasAttribute("novalidate");
        this.uxInputElements = getInputElements({
            container: this.element,
            supportedElements: elementsSelectors
        });
        this.addInputsEventListeners();
        if (!this.formElement) {
            return;
        }
        this.formElement.addEventListener("submit", this.validateForm);
    };

    @Listen("uxPasswordValidated", { target: "window" })
    passwordValidated(e: CustomEvent) {
        this.isValidPassword = e.detail.success;
    }

    @Listen("uxPasswordConfirmed", { target: "window" })
    passwordConfirmed(e: CustomEvent) {
        this.isConfirmedPassword = e.detail.success;
    }

    private addInputsEventListeners = () => {
        this.uxInputElements.forEach((uxInputElement) => {
            waitForHydrated(uxInputElement, () => {
                const inputs = uxInputElement.querySelectorAll("input, select, textarea");
                if (inputs.length === 0) {
                    return;
                }
                inputs.forEach((input) => {
                    input.addEventListener("invalid", this.validateOnInvalid);
                    if (this.validationOnBlur) {
                        input.addEventListener("blur", this.validateOnBlur);
                    }
                });
            });
        });
    };

    private validateForm = async (e: Event | null = null) => {
        if (this.async) {
            this.asyncError = false;
        }
        if (this.skipValidation) {
            if (this.async && e !== null) {
                e?.preventDefault();
                await this.submitAsyncData();
            }
            return;
        }
        this.uxInputElements.forEach((uxInputElement) => {
            this.validateField(uxInputElement);
        });
        this.setIsValid();
        const formData = getFormData({
            uxElements: this.uxInputElements
        });
        const event = this.formValidationEvent.emit({
            isValid: this.isValid,
            serializedFormData: this.formElement ? serializeForm(this.formElement) : null,
            formData: formData
        });
        if (!this.isValid) {
            this.scrollToFirstError({
                uxElements: this.uxInputElements
            });
        }
        if (event.defaultPrevented) {
            e.preventDefault();
        }
        if (this.async && this.isValid && e !== null) {
            e?.preventDefault();
            await this.submitAsyncData();
        }
        if (!this.isValid && e !== null) {
            e.preventDefault();
        }
    };

    private submitAsyncData = async () => {
        if (this.isSubmittingAsync) {
            return;
        }
        const url = this.customAction || this.formElement.action;
        try {
            this.isSubmittingAsync = true;
            const options = isMockUrl(url)
                ? {}
                : {
                      method: "POST",
                      body: serializeForm(this.formElement),
                      headers: {
                          "Content-Type": "application/x-www-form-urlencoded;charset=UTF-8"
                      }
                  };
            const response = await api<string>(url, options);
            if (isMockUrl(url)) {
                await new Promise((resolve) => setTimeout(resolve, 2000));
            }
            this.isSubmittingAsync = false;
            this.element.innerHTML = "";
            this.element.insertAdjacentHTML("afterbegin", response);
            scrollToTarget({
                target: this.element,
                additionalOffsetTop: 30 + this.additionalScrollOffset
            });
            this.initForm();
        } catch (e) {
            this.isSubmittingAsync = false;
            this.asyncError = true;
        }
    };

    private setIsValid = () => {
        this.isValid = !!!this.uxInputElements.find(
            (uxInputElement) =>
                uxInputElement.error === true &&
                !uxInputElement.hasAttribute(this.dataNovalidation) &&
                !uxInputElement.hasAttribute("disabled")
        );
    };

    private resetError = (eTarget: HTMLElement) => {
        if (eTarget.closest("ux-form") !== this.element) {
            return;
        }
        const formElement = eTarget.closest(`ux-form-group`) || eTarget.closest(elementsSelectors.join(", "));
        if (formElement === null) {
            return;
        }
        if (formElement.classList.contains("ux-file-upload")) {
            return;
        }
        /** formElement is an element of all supported ux form elements:
         * HTMLUxInputFieldElement | HTMLUxSelectElement | [...]
         **/
        /* eslint-disable-next-line @typescript-eslint/no-explicit-any */
        (formElement as any).error = false;
    };

    @Listen("change", { target: "window" })
    instantValidation(e: Event) {
        const eTarget = e.target as HTMLElement;
        this.resetError(eTarget);
    }

    @Listen("focusin", { target: "window" })
    resetErrorStatusOnFocus(e: Event) {
        const eTarget = e.target as HTMLElement;
        this.resetError(eTarget);
    }

    private validateFormThrottled = throttle(() => this.validateForm(), 300);

    private validateOnInvalid = (e: Event) => {
        e.preventDefault();
        this.validateFormThrottled();
    };

    private validateOnBlur = (e: Event) => {
        const eTarget = e.target as HTMLElement;
        const uxInputElement = this.uxInputElements.find((uxElement) => uxElement.contains(eTarget));
        this.validateField(uxInputElement);
    };

    /** uxInputElement is an element of all supported ux form elements:
     * HTMLUxInputFieldElement | HTMLUxSelectElement | [...]
     **/
    /* eslint-disable-next-line @typescript-eslint/no-explicit-any */
    private validateField = (uxInputElement: any) => {
        if (uxInputElement.hasAttribute(this.dataNovalidation) || uxInputElement.hasAttribute("disabled")) {
            return;
        }
        const inputElements = Array.from(uxInputElement.querySelectorAll("input, select, textarea")) as HTMLInputElement[];
        const setErrorMessage = getErrorMessageSetter({
            uxInputElement: uxInputElement,
            uxForm: this.element
        });
        if (uxInputElement.classList.contains(`ux-form-group`)) {
            validateFormGroup({
                uxElement: uxInputElement,
                errorSetter: setErrorMessage,
                dataGroup: this.dataGroup,
                dataRequired: this.dataRequired
            });
        }
        const val = getValidationAttributes({
            uxElement: uxInputElement,
            dataLength: this.dataLength,
            dataValue: this.dataValue,
            dataRegex: this.dataRegex
        });
        inputElements.forEach((inputEl) => {
            const value = inputEl.value;
            if (uxInputElement.required === true && inputEl.validity.valueMissing) {
                setErrorMessage(`${this.dataRequired}--msg`);
            } else if (inputEl.validity.typeMismatch || inputEl.validity.badInput) {
                setErrorMessage(`${this.dataInvalid}--msg`);
            } else if (inputEl.validity.patternMismatch) {
                setErrorMessage(`${this.dataInvalid}--msg`);
            } else if (val.minLength !== null && value.length < val.minLength) {
                setErrorMessage(`${this.dataLength}--msg`);
            } else if (val.maxLength !== null && value.length > val.maxLength) {
                setErrorMessage(`${this.dataLength}--msg`);
            } else if (val.minValue !== null && Number(value) < val.minValue) {
                setErrorMessage(`${this.dataValue}--msg`);
            } else if (val.minValue !== null && Number(value) > val.maxValue) {
                setErrorMessage(`${this.dataValue}--msg`);
            } else if (
                uxInputElement.required === true &&
                inputEl.getAttribute("data-password") === "true" &&
                !this.isValidPassword
            ) {
                setErrorMessage(`${this.dataRequired}--msg`);
            } else if (
                uxInputElement.required === true &&
                inputEl.hasAttribute("data-password-confirm") &&
                !this.isConfirmedPassword
            ) {
                setErrorMessage(`${this.dataRequired}--msg`);
            } else if (val.regexExpression !== null) {
                let regex: RegExp;
                if (val.regexFlag !== null) {
                    regex = new RegExp(val.regexExpression, val.regexFlag);
                } else {
                    regex = new RegExp(val.regexExpression);
                }
                if (!value.match(regex)) {
                    setErrorMessage(`${this.dataRegex}--msg`);
                }
            } else if (!inputEl.validity.valid) {
                setErrorMessage(null);
            }
        });
    };

    /** AVOIDS VALIDATION ON HIDDEN ELEMENTS */
    @Listen("uxStaticFilter", { target: "window" })
    handleFilterEvent(e: CustomEvent) {
        const eTarget = e.target as HTMLElement;
        if (!this.element.contains(eTarget)) {
            return;
        }
        const { htmlElement, hidden } = e.detail;
        handleDisabledElements(htmlElement, elementsSelectors, hidden);
    }

    /** scrolls form to first error */
    private scrollToFirstError = ({ uxElements }: { uxElements: HTMLUxInputFieldElement[] }) => {
        const firstElementWithError = uxElements.find((uxInputElement) => {
            return uxInputElement.error;
        });
        if (!firstElementWithError) {
            return;
        }
        const elementTop = firstElementWithError.getBoundingClientRect().top;
        const isInViewport = elementTop < 0;
        if (isInViewport) {
            scrollToTarget({
                target: firstElementWithError,
                additionalOffsetTop: 10 + this.additionalScrollOffset
            });
        }
    };

    @Listen("click", { target: "window" })
    handleSubmitButtonAttributes(e: Event) {
        const eTarget = e.target as HTMLElement;
        if (!this.element.contains(eTarget)) {
            return;
        }
        if (eTarget.hasAttribute("formnovalidate")) {
            this.skipValidation = true;
        } else {
            this.skipValidation = this.noValidation || this.formElement?.hasAttribute("novalidate");
        }
        if (eTarget.hasAttribute("formaction")) {
            this.customAction = eTarget.getAttribute("formaction");
        } else {
            this.customAction = null;
        }
    }

    render() {
        return (
            <Host class={this.baseClass}>
                <slot />
                {this.async && this.isSubmittingAsync && <ux-spinner-overlay opened></ux-spinner-overlay>}
            </Host>
        );
    }
}

function isMockUrl(url: string) {
    return url.includes("mock-data/") || url.includes("Assets/fetchContent");
}
